import { 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { AccessDeviceInfo, AccessDeviceFlags } from '@/lib/deviceInfo';

// Types
export interface Address {
  id: string;
  label: string;
  fullName: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phoneNumber: string;
  isDefault: boolean;
}

export interface NotificationPreferences {
  orderUpdates: boolean;
  promotions: boolean;
  newsletters: boolean;
}

export interface UserProfile {
  uid: string;
  email: string | null;
  name: string;
  addresses: Address[];
  notificationPreferences: NotificationPreferences;
  createdAt: Date;
  // LTV-related fields (optional since they're calculated by backend)
  firstOrderDate?: Date;
  lastOrderDate?: Date;
  totalSpent?: number;
  orderCount?: number;
  averageOrderValue?: number;
  averageDaysBetweenOrders?: number;
  calculatedLTV?: number;
  customerSegment?: 'new' | 'returning' | 'loyal';
  isActive?: boolean;
  // Affiliate program field
  isAffiliate?: boolean;
  lastAccessDevice?: StoredAccessDeviceInfo;
  accessDeviceOrigins?: AccessDeviceFlags;
}

export interface StoredAccessDeviceInfo {
  platform?: string;
  surface?: string;
  formFactor?: string;
  userAgent?: string;
  appVersion?: string;
  recordedAt?: Date;
  flags?: AccessDeviceFlags;
}

const pruneUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>;
};

/**
 * Get a user profile by ID
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    
    // If user exists but doesn't have createdAt, add it
    if (!userData.createdAt) {
      await updateDoc(userRef, {
        createdAt: serverTimestamp()
      });
      // Refresh the document to get the updated data
      const updatedDoc = await getDoc(userRef);
      if (updatedDoc.exists()) {
        userData.createdAt = updatedDoc.data().createdAt;
      }
    }
    
    // Helper function to convert Firestore timestamp to Date
    const convertTimestamp = (timestamp: any): Date => {
      if (!timestamp) {
        return new Date();
      }
      
      // Handle Firestore Timestamp objects
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      
      // Handle regular Date objects
      if (timestamp instanceof Date) {
        return timestamp;
      }
      
      // Handle string dates
      if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? new Date() : date;
      }
      
      // Handle number timestamps
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      
      // Fallback to current date
      return new Date();
    };
    
    return {
      uid: userDoc.id,
      email: userData.email || null,
      name: userData.name || '',
      addresses: userData.addresses || [],
      notificationPreferences: userData.notificationPreferences || {
        orderUpdates: false,
        promotions: false,
        newsletters: false
      },
      createdAt: convertTimestamp(userData.createdAt),
      // LTV-related fields (optional)
      firstOrderDate: userData.firstOrderDate ? convertTimestamp(userData.firstOrderDate) : undefined,
      lastOrderDate: userData.lastOrderDate ? convertTimestamp(userData.lastOrderDate) : undefined,
      totalSpent: userData.totalSpent,
      orderCount: userData.orderCount,
      averageOrderValue: userData.averageOrderValue,
      averageDaysBetweenOrders: userData.averageDaysBetweenOrders,
      calculatedLTV: userData.calculatedLTV,
      customerSegment: userData.customerSegment,
      isActive: userData.isActive,
      lastAccessDevice: userData.lastAccessDevice ? {
        ...userData.lastAccessDevice,
        recordedAt: convertTimestamp(userData.lastAccessDevice.recordedAt)
      } : undefined,
      accessDeviceOrigins: userData.accessDeviceOrigins
    };
  } catch (error) {
    throw new Error('Failed to get user profile');
  }
};

/**
 * Add a new address to user profile
 */
export const addAddress = async (userId: string, address: Omit<Address, 'id'>): Promise<Address> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    // Create a new document if user doesn't exist in Firestore yet
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }
    
    // Generate a unique ID for the address
    const newId = Date.now().toString();
    const newAddress: Address = {
      ...address,
      id: newId,
    };
    
    const userData = userDoc.data();
    const currentAddresses = userData.addresses || [];
    
    // If this is the default address, update all others to non-default
    let updatedAddresses = [...currentAddresses];
    if (newAddress.isDefault) {
      updatedAddresses = updatedAddresses.map(addr => ({
        ...addr,
        isDefault: false
      }));
      updatedAddresses.push(newAddress);
    } else {
      updatedAddresses.push(newAddress);
    }
    
    // Update the user document with new address array
    await updateDoc(userRef, {
      addresses: updatedAddresses
    });
    
    return newAddress;
  } catch (error) {
    throw new Error('Failed to add address');
  }
};

/**
 * Update an existing address
 */
export const updateAddress = async (userId: string, address: Address): Promise<Address> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }
    
    const userData = userDoc.data();
    const currentAddresses = userData.addresses || [];
    
    // Update the address and handle default address logic
    let updatedAddresses;
    if (address.isDefault) {
      // If this address is being set as default, update all addresses
      updatedAddresses = currentAddresses.map(addr => {
        if (addr.id === address.id) {
          // Update this address with all new data and set as default
          return { ...address, isDefault: true };
        } else {
          // Set all other addresses as non-default
          return { ...addr, isDefault: false };
        }
      });
    } else {
      // If not setting as default, just update this address
      updatedAddresses = currentAddresses.map(addr => 
        addr.id === address.id ? address : addr
      );
    }
    
    // Update the user document
    await updateDoc(userRef, {
      addresses: updatedAddresses
    });
    
    return address;
  } catch (error) {
    throw new Error('Failed to update address');
  }
};

/**
 * Delete an address
 */
export const deleteAddress = async (userId: string, addressId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }
    
    const userData = userDoc.data();
    const currentAddresses = userData.addresses || [];
    const addressToRemove = currentAddresses.find(addr => addr.id === addressId);
    
    if (!addressToRemove) {
      throw new Error('Address not found');
    }
    
    // Filter out the deleted address
    const updatedAddresses = currentAddresses.filter(addr => addr.id !== addressId);
    
    // If we removed the default address and there are other addresses, make the first one default
    if (addressToRemove.isDefault && updatedAddresses.length > 0) {
      updatedAddresses[0].isDefault = true;
    }
    
    // Update the user document
    await updateDoc(userRef, {
      addresses: updatedAddresses
    });
  } catch (error) {
    throw new Error('Failed to delete address');
  }
};

/**
 * Update user profile information
 */
export const updateUserProfile = async (userId: string, updates: Partial<Omit<UserProfile, 'uid' | 'addresses'>>): Promise<UserProfile> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }
    
    // Update the user document
    await updateDoc(userRef, updates);
    
    // Get the updated user profile
    const updatedUserDoc = await getDoc(userRef);
    const userData = updatedUserDoc.data();
    
    return {
      uid: updatedUserDoc.id,
      email: userData.email || null,
      name: userData.name || '',
      addresses: userData.addresses || [],
      notificationPreferences: userData.notificationPreferences || {
        orderUpdates: false,
        promotions: false,
        newsletters: false
      },
      createdAt: userData.createdAt || new Date(),
      // LTV-related fields (optional)
      firstOrderDate: userData.firstOrderDate ? new Date(userData.firstOrderDate) : undefined,
      lastOrderDate: userData.lastOrderDate ? new Date(userData.lastOrderDate) : undefined,
      totalSpent: userData.totalSpent,
      orderCount: userData.orderCount,
      averageOrderValue: userData.averageOrderValue,
      averageDaysBetweenOrders: userData.averageDaysBetweenOrders,
      calculatedLTV: userData.calculatedLTV,
      customerSegment: userData.customerSegment,
      isActive: userData.isActive
    };
  } catch (error) {
    throw new Error('Failed to update user profile');
  }
};

/**
 * Update notification preferences
 */
export const updateNotificationPreferences = async (userId: string, preferences: NotificationPreferences): Promise<NotificationPreferences> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }
    
    // Update the user document with new notification preferences
    await updateDoc(userRef, {
      notificationPreferences: preferences
    });
    
    return preferences;
  } catch (error) {
    throw new Error('Failed to update notification preferences');
  }
};

/**
 * Record the device/surface a user used to access the app.
 */
export const markUserAccessDevice = async (userId: string, deviceInfo: AccessDeviceInfo): Promise<void> => {
  const userRef = doc(db, 'users', userId);

  const accessDevice = pruneUndefined({
    platform: deviceInfo.platform,
    surface: deviceInfo.surface,
    formFactor: deviceInfo.formFactor,
    userAgent: deviceInfo.userAgent,
    appVersion: deviceInfo.appVersion,
    recordedAt: serverTimestamp(),
    flags: deviceInfo.flags
  });

  const surfaceFlags: AccessDeviceFlags = {
    desktop: deviceInfo.surface === 'desktop_browser',
    mobileBrowser: deviceInfo.surface === 'mobile_browser',
    mobileApp: deviceInfo.surface === 'mobile_app_webview'
  };

  const platformFlags: AccessDeviceFlags = {
    ios: deviceInfo.platform === 'ios',
    android: deviceInfo.platform === 'android',
    macos: deviceInfo.platform === 'macos',
    windows: deviceInfo.platform === 'windows',
    linux: deviceInfo.platform === 'linux'
  };

  const mergedFlags: AccessDeviceFlags = {
    ...deviceInfo.flags,
    ...surfaceFlags,
    ...platformFlags
  };

  const flagsToPersist = Object.fromEntries(
    Object.entries(mergedFlags).filter(([, value]) => value)
  );

  await setDoc(userRef, pruneUndefined({
    lastAccessDevice: accessDevice,
    accessDeviceOrigins: Object.keys(flagsToPersist).length ? flagsToPersist : undefined
  }), { merge: true });
};

/**
 * Create a new user profile
 */
export const createUserProfile = async (
  userId: string, 
  userData: {
    email: string | null;
    name: string;
    notificationPreferences?: NotificationPreferences;
  }
): Promise<UserProfile> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Check if user already exists
    const existingUser = await getDoc(userRef);

    const defaults = {
      addresses: [],
      notificationPreferences: userData.notificationPreferences || {
        orderUpdates: true,
        promotions: true,
        newsletters: false
      },
      isActive: false
    };

    // Upsert profile: if it exists, merge provided + defaults; if not, create
    const payload: any = {
      uid: userId,
      email: userData.email,
      name: userData.name,
      ...defaults,
      // Preserve createdAt if it exists; otherwise set it
      createdAt: existingUser.exists() ? existingUser.data()?.createdAt || serverTimestamp() : serverTimestamp()
    };

    // Merge to avoid clobbering other fields (addresses, etc.) if already present
    await setDoc(userRef, payload, { merge: true });

    // Return the upserted user profile
    return await getUserProfile(userId) as UserProfile;
  } catch (error) {
    throw new Error('Failed to create user profile');
  }
}; 
